import { color, font } from '../styles/tokens';
import { lookupWhois, lookupDns, lookupIp, lookupCve, lookupSanctions } from './reconClient';
import { lookupBtcWallet, lookupEthWallet } from './walletLookup';
import { registerPanel } from '../map/panelStack';

/**
 * Painel RECON — "terminal de consulta" no HUD, canto inferior direito.
 * Diferente de todas as outras camadas do projeto: não é DataSource,
 * não entra no LayerOrchestrator, não gera Entity nem marker no mapa.
 * É consulta pontual: usuário escolhe um tipo, digita um valor, recebe
 * resultado — sem polling, sem estado persistente entre consultas.
 *
 * Escopo desta fase: whois, dns, ip, cve, sanctions (via backend) e
 * btc/eth (direto do client). Port scanner FICA DE FORA — é o item de
 * maior risco do módulo e foi deliberadamente deixado pra uma decisão
 * explícita separada, não incluído por padrão nesta entrega.
 *
 * CORREÇÃO (diagnóstico de layout):
 * - Espaçamento (item 1): marginBottom alternava entre 8px/6px/6px/8px
 *   sem critério entre título/select/input/button — convergido para
 *   --space-2 (8px) consistentemente em todos os 4.
 * - Estados visuais (item 5): botão CONSULTAR já desabilita de fato
 *   durante a consulta (`button.disabled`, ver handleSubmit) mas não
 *   tinha NENHUM feedback visual de :disabled, :active, ou
 *   :focus-visible — nova classe `.recon-submit-button` (global.css)
 *   adiciona os 3 estados que faltavam.
 */

type ReconQueryType = 'whois' | 'dns' | 'ip' | 'cve' | 'sanctions' | 'btc' | 'eth';

const QUERY_LABELS: Record<ReconQueryType, string> = {
  whois: 'WHOIS (domínio)',
  dns: 'DNS (domínio)',
  ip: 'IP INTEL (endereço)',
  cve: 'CVE (ID ou palavra-chave)',
  sanctions: 'SANÇÕES OFAC/SDN (nome)',
  btc: 'CARTEIRA BTC (endereço)',
  eth: 'CARTEIRA ETH (endereço)',
};

function formatLine(label: string, value: string): string {
  return `${label.padEnd(18)} ${value}`;
}

async function runQuery(type: ReconQueryType, value: string): Promise<string> {
  switch (type) {
    case 'whois': {
      const r = await lookupWhois(value);
      const lines = [
        formatLine('domínio', r.domain),
        formatLine('status', r.status.join(', ') || '—'),
        formatLine('nameservers', r.nameservers.join(', ') || '—'),
        formatLine('dnssec', r.dnssecSigned === null ? '—' : r.dnssecSigned ? 'sim' : 'não'),
        formatLine('registrado em', r.registrationDate ?? '—'),
        formatLine('expira em', r.expirationDate ?? '—'),
        formatLine('últ. alteração', r.lastChangedDate ?? '—'),
      ];
      for (const e of r.entities) lines.push(formatLine(`entidade (${e.role})`, e.name ?? '—'));
      return lines.join('\n');
    }

    case 'dns': {
      const r = await lookupDns(value);
      if (r.status !== 0) return formatLine('erro', r.statusMessage ?? `status ${r.status}`);
      if (r.records.length === 0) return 'Nenhum registro A encontrado.';
      return r.records.map((rec) => formatLine(`TTL ${rec.ttl}s`, rec.data)).join('\n');
    }

    case 'ip': {
      const r = await lookupIp(value);
      return [
        formatLine('endereço', r.address),
        formatLine('país', `${r.country} (${r.countryCode})`),
        formatLine('região/cidade', `${r.region} / ${r.city}`),
        formatLine('isp', r.isp),
        formatLine('organização', r.org),
        formatLine('asn', `${r.asn} — ${r.asName}`),
        formatLine('timezone', r.timezone),
        formatLine('sinalizadores', [r.isMobile && 'mobile', r.isProxy && 'proxy', r.isHosting && 'hosting'].filter(Boolean).join(', ') || '—'),
      ].join('\n');
    }

    case 'cve': {
      const isId = /^CVE-\d{4}-\d{4,}$/i.test(value);
      const results = await lookupCve(isId ? { id: value } : { keyword: value });
      if (results.length === 0) return 'Nenhum CVE encontrado.';
      return results
        .map((r) =>
          [
            formatLine('CVE', r.id),
            formatLine('status', r.status),
            formatLine('CVSS', r.cvssScore !== null ? `${r.cvssScore} (${r.cvssSeverity}, ${r.cvssVersion})` : '—'),
            formatLine('descrição', r.description ?? '—'),
          ].join('\n'),
        )
        .join('\n\n');
    }

    case 'sanctions': {
      const { matches, truncated } = await lookupSanctions(value);
      if (matches.length === 0) return 'Nenhuma correspondência encontrada na lista OFAC/SDN.';
      const lines = matches.map((m) =>
        formatLine(m.schema, `${m.caption}${m.nationality.length ? ` [${m.nationality.join(', ')}]` : ''}`),
      );
      if (truncated) lines.push('(resultados truncados — refine a busca)');
      return lines.join('\n');
    }

    case 'btc': {
      const r = await lookupBtcWallet(value);
      return [
        formatLine('endereço', r.address),
        formatLine('saldo confirmado', `${r.confirmedBalanceSats} sats`),
        formatLine('saldo não confirmado', `${r.unconfirmedBalanceSats} sats`),
        formatLine('total de tx', String(r.totalTxCount)),
      ].join('\n');
    }

    case 'eth': {
      const r = await lookupEthWallet(value);
      return [
        formatLine('endereço', r.address),
        formatLine('saldo', `${r.balanceEth} ETH (${r.balanceWei} wei)`),
        formatLine('tx count', r.txCount !== null ? String(r.txCount) : '—'),
        formatLine('é contrato', r.isContract ? 'sim' : 'não'),
        formatLine('ENS', r.ensName ?? '—'),
      ].join('\n');
    }
  }
}

export function mountReconPanel(hudRoot: HTMLElement): void {
  const panel = document.createElement('div');
  panel.className = 'hud-panel side-panel side-panel--recon hud-interactive';

  const title = document.createElement('div');
  title.className = 'label-secondary';
  title.textContent = 'RECON';
  title.style.marginBottom = 'var(--space-2)';

  const select = document.createElement('select');
  select.className = 'touch-target';
  select.style.width = '100%';
  select.style.marginBottom = 'var(--space-2)';
  select.style.background = color.bgPanel;
  select.style.color = color.textPrimary;
  select.style.border = `1px solid ${color.borderHairline}`;
  select.style.fontFamily = font.body;
  select.style.fontSize = '16px'; // ≥16px evita zoom automático do Safari iOS ao focar (confirmado: threshold real é o font-size computado, qualquer valor <16px dispara)
  select.style.padding = '4px 8px';
  for (const [value, label] of Object.entries(QUERY_LABELS)) {
    const option = document.createElement('option');
    option.value = value;
    option.textContent = label;
    select.appendChild(option);
  }

  const input = document.createElement('input');
  input.type = 'text';
  input.placeholder = 'valor da consulta';
  input.className = 'touch-target';
  input.style.width = '100%';
  input.style.marginBottom = 'var(--space-2)';
  input.style.background = color.bgPanel;
  input.style.color = color.textPrimary;
  input.style.border = `1px solid ${color.borderHairline}`;
  input.style.fontFamily = font.mono;
  input.style.fontSize = '16px'; // ≥16px evita zoom automático do Safari iOS ao focar — confirmado, não é suposição
  input.style.padding = '6px 8px';
  input.style.boxSizing = 'border-box';

  const button = document.createElement('button');
  button.textContent = 'CONSULTAR';
  button.className = 'touch-target recon-submit-button';
  button.style.width = '100%';
  button.style.marginBottom = 'var(--space-2)';
  button.style.fontFamily = font.heading;
  button.style.fontWeight = '600';
  button.style.fontSize = '13px';
  button.style.padding = '6px';

  const output = document.createElement('pre');
  output.className = 'mono';
  output.style.margin = '0';
  output.style.whiteSpace = 'pre-wrap';
  output.style.wordBreak = 'break-word';
  output.style.fontSize = '12px';
  output.style.color = color.textPrimary;
  output.style.maxHeight = '240px';
  output.style.overflowY = 'auto';
  output.textContent = 'Aguardando consulta.';

  async function handleSubmit(): Promise<void> {
    const value = input.value.trim();
    if (!value) return;

    const type = select.value as ReconQueryType;
    button.disabled = true;
    output.textContent = 'Consultando...';

    try {
      output.textContent = await runQuery(type, value);
    } catch (err) {
      output.textContent = `Erro: ${err instanceof Error ? err.message : 'falha desconhecida'}`;
    } finally {
      button.disabled = false;
    }
  }

  button.addEventListener('click', () => void handleSubmit());
  input.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter') void handleSubmit();
  });

  panel.append(title, select, input, button, output);
  hudRoot.appendChild(panel);
  registerPanel('recon', panel, true); // aberto por padrão no tablet; fechado no mobile até o usuário abrir via chip
}
